
class LonglashStickyAtc extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.section = this.closest('.shopify-section');
    this.purchaseRegion = this.section?.querySelector('[data-ll-main-purchase]');
    this.stickyBar = this.querySelector('[data-ll-sticky-bar]');
    this.stickyPrice = this.querySelector('[data-ll-sticky-price]');
    this.stickyButton = this.querySelector('[data-ll-sticky-button]');

    if (!this.section || !this.purchaseRegion || !this.stickyBar || !this.stickyButton) {
      this.hidden = true;
      return;
    }

    this.handleStickyClick = this.handleStickyClick.bind(this);
    this.stickyButton.addEventListener('click', this.handleStickyClick);

    this.visibilityObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      const shouldShow = Boolean(entry && !entry.isIntersecting);
      this.stickyBar.classList.toggle('is-visible', shouldShow);
      this.stickyBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    }, { threshold: 0.08 });
    this.visibilityObserver.observe(this.purchaseRegion);

    this.stateObserver = new MutationObserver(() => this.syncFromNativeForm());
    this.stateObserver.observe(this.purchaseRegion, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
      attributeFilter: ['class', 'disabled', 'aria-disabled'],
    });

    this.syncFromNativeForm();
  }

  get nativeButton() {
    return this.purchaseRegion?.querySelector('form[data-type="add-to-cart-form"] button[name="add"]');
  }

  syncFromNativeForm() {
    const nativeButton = this.nativeButton;
    const nativeLabel = nativeButton?.querySelector('[data-submit-button]');
    const nativePrice = this.purchaseRegion?.querySelector('[id^="Price-"]');

    if (!nativeButton) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.stickyButton.disabled = nativeButton.disabled || nativeButton.getAttribute('aria-disabled') === 'true';
    this.stickyButton.textContent = nativeLabel?.textContent?.trim() || nativeButton.textContent.trim();

    if (nativePrice && this.stickyPrice) {
      this.stickyPrice.textContent = nativePrice.textContent.replace(/\s+/g, ' ').trim();
    }
  }

  handleStickyClick(event) {
    event.preventDefault();
    const nativeButton = this.nativeButton;
    if (nativeButton && !nativeButton.disabled) nativeButton.click();
  }

  disconnectedCallback() {
    this.stickyButton?.removeEventListener('click', this.handleStickyClick);
    this.visibilityObserver?.disconnect();
    this.stateObserver?.disconnect();
    this.initialized = false;
  }
}

if (!customElements.get('longlash-sticky-atc')) {
  customElements.define('longlash-sticky-atc', LonglashStickyAtc);
}

class LonglashBundleSelector extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleNativeVariantChange = this.handleNativeVariantChange.bind(this);
    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange);
    this.initializeAttempts = 0;
    this.initializeWhenReady();
  }

  initializeWhenReady() {
    this.productInfo = this.closest('product-info');
    this.section = this.closest('.shopify-section');
    this.form = this.productInfo?.querySelector('form[data-type="add-to-cart-form"]');
    this.variantInput = this.form?.elements.namedItem('id');
    this.priceContainer = this.productInfo?.querySelector('[id^="Price-"]');
    this.quantityInput = this.productInfo?.querySelector('.quantity-input');
    this.stickyTitle = this.section?.querySelector('[data-ll-sticky-title]');

    if (!this.form || !this.variantInput || !this.priceContainer) {
      this.initializeAttempts += 1;
      if (this.initializeAttempts < 20) {
        this.initializeTimer = window.setTimeout(() => this.initializeWhenReady(), 50);
      }
      return;
    }

    this.originalPriceHtml = this.priceContainer.innerHTML;
    this.originalTitle = this.stickyTitle?.textContent?.trim() || '';
    this.form.addEventListener('variant:change', this.handleNativeVariantChange);

    const selectedOption = this.querySelector('[data-ll-bundle-option].is-selected')
      || this.querySelector('[data-ll-bundle-option][data-original-option]');
    if (selectedOption) this.applyOption(selectedOption, { updatePrice: false });
  }

  get selectedOption() {
    return this.querySelector('[data-ll-bundle-option].is-selected');
  }

  handleClick(event) {
    const option = event.target.closest('[data-ll-bundle-option]');
    if (!option || !this.contains(option) || option.disabled || option.dataset.available !== 'true') return;
    this.applyOption(option);
  }

  handleChange(event) {
    const select = event.target.closest('[data-ll-bundle-variant-select]');
    if (!select || !this.contains(select)) return;

    const picker = select.closest('[data-ll-bundle-variant-picker]');
    const option = this.querySelector(`[data-ll-bundle-option][data-product-id="${picker?.dataset.productId}"]`);
    if (!option) return;

    this.syncOptionFromVariantSelect(option, select);
    if (option.classList.contains('is-selected')) this.applyOption(option);
  }

  syncOptionFromVariantSelect(option, select) {
    const selectedVariant = select.selectedOptions[0];
    if (!selectedVariant) return;

    option.dataset.variantId = selectedVariant.value;
    option.dataset.available = selectedVariant.dataset.available;
    option.dataset.quantityMin = selectedVariant.dataset.quantityMin || '1';
    option.dataset.quantityMax = selectedVariant.dataset.quantityMax || '';
    option.dataset.quantityIncrement = selectedVariant.dataset.quantityIncrement || '1';

    const note = option.querySelector('.ll-pdp__bundle-copy small');
    if (note) note.textContent = selectedVariant.dataset.title || '';

    const price = option.querySelector('[data-ll-bundle-price]');
    if (price) price.textContent = selectedVariant.dataset.price || '';

    const compare = option.querySelector('[data-ll-bundle-compare]');
    const comparePrice = selectedVariant.dataset.comparePrice || '';
    if (compare) {
      compare.textContent = comparePrice;
      compare.hidden = !comparePrice;
    }
  }

  applyOption(option, { updatePrice = true } = {}) {
    if (!this.form || !this.variantInput || !option) return;

    const variantSelect = this.querySelector(
      `[data-ll-bundle-variant-picker][data-product-id="${option.dataset.productId}"] [data-ll-bundle-variant-select]`
    );
    if (variantSelect) this.syncOptionFromVariantSelect(option, variantSelect);

    const available = option.dataset.available === 'true';
    const variantId = option.dataset.variantId;
    if (!available || !variantId) return;

    this.querySelectorAll('[data-ll-bundle-option]').forEach((item) => {
      const selected = item === option;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-pressed', selected ? 'true' : 'false');
      const choice = item.querySelector('.ll-pdp__bundle-choice');
      if (choice) choice.replaceChildren(...(selected ? [document.createElement('span')] : []));
    });

    this.querySelectorAll('[data-ll-bundle-variant-picker]').forEach((picker) => {
      picker.hidden = picker.dataset.productId !== option.dataset.productId;
    });

    this.variantInput.disabled = false;
    this.variantInput.value = variantId;
    this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.updateQuantity(option);
    this.updateSubmitState(true);

    if (updatePrice) this.updatePrice(option);
    if (this.stickyTitle) this.stickyTitle.textContent = option.dataset.productTitle || this.originalTitle;

    this.dispatchEvent(new CustomEvent('longlash:bundle-selection', {
      bubbles: true,
      detail: {
        productId: option.dataset.productId,
        variantId,
        originalProduct: option.hasAttribute('data-original-option'),
      },
    }));
  }

  updatePrice(option) {
    if (!this.priceContainer) return;

    if (option.hasAttribute('data-original-option')) {
      this.priceContainer.innerHTML = this.originalPriceHtml;
      return;
    }

    const template = this.querySelector(
      `[data-ll-bundle-price-template][data-variant-id="${option.dataset.variantId}"]`
    );
    if (template) this.priceContainer.replaceChildren(template.content.cloneNode(true));
  }

  updateQuantity(option) {
    if (!this.quantityInput) return;

    const minimum = Number.parseInt(option.dataset.quantityMin || '1', 10);
    const increment = Number.parseInt(option.dataset.quantityIncrement || '1', 10);
    const maximum = Number.parseInt(option.dataset.quantityMax || '', 10);
    let value = Number.parseInt(this.quantityInput.value || String(minimum), 10);

    this.quantityInput.min = String(minimum);
    this.quantityInput.dataset.min = String(minimum);
    this.quantityInput.step = String(increment);

    if (Number.isNaN(maximum)) {
      this.quantityInput.removeAttribute('max');
      delete this.quantityInput.dataset.max;
    } else {
      this.quantityInput.max = String(maximum);
      this.quantityInput.dataset.max = String(maximum);
      value = Math.min(value, maximum);
    }

    value = Math.max(value, minimum);
    this.quantityInput.value = String(value);
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  updateSubmitState(available) {
    if (typeof this.form.toggleSubmitButton === 'function') {
      this.form.toggleSubmitButton(!available, available ? undefined : window.variantStrings?.soldOut);
      return;
    }

    const button = this.form.querySelector('button[name="add"]');
    if (!button) return;
    button.disabled = !available;
    const label = button.querySelector('[data-submit-button]');
    if (label) label.textContent = available
      ? (window.variantStrings?.addToCart || 'Adaugă în coș')
      : (window.variantStrings?.soldOut || 'Epuizat');
  }

  handleNativeVariantChange(event) {
    const variant = event.detail?.variant;
    const originalOption = this.querySelector('[data-ll-bundle-option][data-original-option]');
    if (!variant || !originalOption) return;

    const sourceOption = event.detail?.parsedHTML?.querySelector(
      'longlash-bundle-selector [data-ll-bundle-option][data-original-option]'
    );
    if (sourceOption) {
      [...sourceOption.attributes].forEach((attribute) => {
        if (attribute.name.startsWith('data-')) originalOption.setAttribute(attribute.name, attribute.value);
      });
      const sourcePrice = sourceOption.querySelector('.ll-pdp__bundle-price, .ll-pdp__bundle-placeholder');
      const currentPrice = originalOption.querySelector('.ll-pdp__bundle-price, .ll-pdp__bundle-placeholder');
      if (sourcePrice && currentPrice) currentPrice.replaceWith(sourcePrice.cloneNode(true));
    } else {
      originalOption.dataset.variantId = String(variant.id);
      originalOption.dataset.available = String(Boolean(variant.available));
      originalOption.dataset.quantityMin = String(variant.quantity_rule?.min || 1);
      originalOption.dataset.quantityMax = variant.quantity_rule?.max == null ? '' : String(variant.quantity_rule.max);
      originalOption.dataset.quantityIncrement = String(variant.quantity_rule?.increment || 1);
    }

    this.originalPriceHtml = this.priceContainer?.innerHTML || this.originalPriceHtml;
    const activeOption = this.selectedOption;
    if (activeOption?.hasAttribute('data-original-option')) {
      this.variantInput.value = String(variant.id);
      this.updateQuantity(originalOption);
      this.updateSubmitState(Boolean(variant.available));
      return;
    }

    if (activeOption) window.requestAnimationFrame(() => this.applyOption(activeOption));
  }

  disconnectedCallback() {
    window.clearTimeout(this.initializeTimer);
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleChange);
    this.form?.removeEventListener('variant:change', this.handleNativeVariantChange);
    this.initialized = false;
  }
}

if (!customElements.get('longlash-bundle-selector')) {
  customElements.define('longlash-bundle-selector', LonglashBundleSelector);
}

const normalizeLonglashText = (value) => value?.replace(/\s+/g, ' ').trim().toLocaleLowerCase('ro') || '';

function hideLonglashCountdowns(root = document) {
  const headingSelector = 'h1, h2, h3, h4, h5, h6, [role="heading"]';
  const headings = [
    ...(root.matches?.(headingSelector) ? [root] : []),
    ...(root.querySelectorAll?.(headingSelector) || []),
  ];

  headings.forEach((heading) => {
    if (!normalizeLonglashText(heading.textContent).includes('utilizează codul new10')) return;

    let countdown = heading;
    for (let depth = 0; countdown && depth < 6; depth += 1, countdown = countdown.parentElement) {
      const text = normalizeLonglashText(countdown.textContent);
      const hasTimerLabels = ['zile', 'ore', 'min', 'sec'].every((label) => text.includes(label));

      if (hasTimerLabels) {
        countdown.hidden = true;
        countdown.setAttribute('data-ll-hidden-countdown', 'true');
        break;
      }
    }
  });
}

function initializeLonglashAppCleanup() {
  if (!document.body.classList.contains('ll-pdp-page')) return;

  hideLonglashCountdowns();

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) hideLonglashCountdowns(node);
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

function initializeLonglashGalleryCounts(root = document) {
  root.querySelectorAll?.('.ll-pdp__gallery').forEach((gallery) => {
    if (gallery.dataset.llGalleryCountReady === 'true') return;

    const output = gallery.querySelector('[data-ll-gallery-current]');
    const slideshow = gallery.querySelector('.product-media-slideshow');
    if (!output || !slideshow) return;

    const update = () => {
      const activeSlide = slideshow.querySelector('.product-media.swiper-slide-active')
        || slideshow.querySelector('.product-media');
      const index = Number.parseInt(activeSlide?.dataset.index || '0', 10);
      output.textContent = String(Number.isNaN(index) ? 1 : index + 1);
    };

    const observer = new MutationObserver(update);
    observer.observe(slideshow, { attributes: true, subtree: true, attributeFilter: ['class'] });
    gallery.dataset.llGalleryCountReady = 'true';
    update();
  });
}

function initializeLonglashReviewProof(root = document) {
  root.querySelectorAll?.('[data-ll-review-proof]').forEach((output) => {
    if (output.dataset.llReviewReady === 'true') return;

    const productInfo = output.closest('.ll-pdp__info');
    if (!productInfo) return;

    let observedRegion = productInfo;
    const observer = new MutationObserver(() => sync());

    const sync = () => {
      const sourceBadge = productInfo.querySelector('.product-app-block .jdgm-prev-badge');
      if (!sourceBadge) return false;

      output.replaceChildren(sourceBadge.cloneNode(true));
      output.hidden = false;
      output.dataset.llReviewReady = 'true';

      const sourceRegion = sourceBadge.closest('.product-app-block');
      if (sourceRegion && sourceRegion !== observedRegion) {
        observer.disconnect();
        observedRegion = sourceRegion;
        observer.observe(observedRegion, { childList: true, subtree: true, characterData: true });
      }

      return true;
    };

    observer.observe(observedRegion, { childList: true, subtree: true, characterData: true });
    sync();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeLonglashAppCleanup();
    initializeLonglashGalleryCounts();
    initializeLonglashReviewProof();
  }, { once: true });
} else {
  initializeLonglashAppCleanup();
  initializeLonglashGalleryCounts();
  initializeLonglashReviewProof();
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('longlash-sticky-atc').forEach((element) => element.connectedCallback());
  hideLonglashCountdowns(event.target);
  initializeLonglashGalleryCounts(event.target);
  initializeLonglashReviewProof(event.target);
});
